import { Box, Card as MuiCard, Typography } from '@mui/material';
import type { LucideIcon } from 'lucide-react';
import { useId, type ReactElement, type ReactNode } from 'react';

import { Icon } from './Icon';

/**
 * Carte de contenu (maquette « carte » : rayon 16, bordure, ombre posee).
 *
 * Elle porte l'en-tete commun des blocs de la maquette — pastille d'icone,
 * titre, sous-titre, actions a droite — pour que chaque ecran ne le refasse pas
 * avec ses propres marges. Avec un titre, la carte est une **region nommee**
 * (`<section aria-labelledby>`) : un lecteur d'ecran la retrouve dans la liste
 * des reperes, au lieu d'un empilement de textes sans rattachement.
 */
export interface CardProps {
  readonly title?: string;
  readonly subtitle?: ReactNode;
  readonly icon?: LucideIcon;
  /** Boutons ou liens alignes a droite du titre. */
  readonly actions?: ReactNode;
  /** Niveau du titre dans la page ; `h2` sous l'en-tete de page (`h1`). */
  readonly headingLevel?: 'h2' | 'h3';
  readonly children?: ReactNode;
}

export const Card = ({ title, subtitle, icon, actions, headingLevel = 'h2', children }: CardProps): ReactElement => {
  const titleId = useId();
  const hasHeader = title !== undefined || actions !== undefined;

  return (
    <MuiCard
      component={title === undefined ? 'div' : 'section'}
      aria-labelledby={title === undefined ? undefined : titleId}
      sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}
    >
      {hasHeader ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          {icon === undefined ? null : (
            <Box
              sx={{
                display: 'inline-flex',
                p: 1,
                borderRadius: '10px',
                bgcolor: 'tokens.primarySoft',
                color: 'tokens.primary',
              }}
            >
              <Icon icon={icon} size={18} />
            </Box>
          )}
          <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
            {title === undefined ? null : (
              <Typography id={titleId} component={headingLevel} sx={{ fontSize: 16, fontWeight: 700, lineHeight: '22px' }}>
                {title}
              </Typography>
            )}
            {subtitle === undefined ? null : (
              <Typography component="div" sx={{ fontSize: 13, lineHeight: '18px', color: 'tokens.textMuted' }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          {actions === undefined ? null : (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>{actions}</Box>
          )}
        </Box>
      ) : null}
      {children}
    </MuiCard>
  );
};
